import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/fr';
import { groceryAPI, mealAPI } from '../services/api';
import { useCurrentList } from '../context/CurrentListContext';
import { DashboardStats, Meal } from '../types';
import MobileMenu from '../components/MobileMenu';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import MealPreview from '../components/meals/MealPreview';
import { MEAL_CATEGORIES } from '../utils/constants';

moment.locale('fr');

interface DashboardProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

const CATEGORY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  'Repas rapide':      { bg: '#e0f7fa', border: '#0097a7', text: '#006064' },
  'Dîner familial':    { bg: '#fffde7', border: '#f9a825', text: '#e65100' },
  'Occasion spéciale': { bg: '#fce4ec', border: '#e91e63', text: '#880e4f' },
  'Restes':            { bg: '#f1f8e9', border: '#558b2f', text: '#33691e' },
  'Restaurant':        { bg: '#fbe9e7', border: '#e64a19', text: '#bf360c' },
};

const MEAL_CATEGORY_EMOJI: Record<string, string> = {
  'Repas rapide': '⚡',
  'Dîner familial': '🍽️',
  'Occasion spéciale': '🎉',
  'Restes': '♻️',
  'Restaurant': '🏪',
};

const getCategoryEmoji = (category: string) =>
  MEAL_CATEGORIES.find(c => c.name === category)?.emoji ?? '🍽️';

// ── This-week row with eye icon ──────────────────────────────────────────────

const MealRow: React.FC<{
  meal: Meal;
  onViewChange: (v: string) => void;
  onPreview: (meal: Meal) => void;
}> = ({ meal, onViewChange, onPreview }) => (
  <div className="flex items-start gap-2 group">
    <button
      type="button"
      onClick={() => onViewChange('planner')}
      className="flex items-start gap-2 text-left flex-1 min-w-0 hover:bg-primary/5 rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors duration-150"
    >
      <span className="text-base mt-0.5 flex-shrink-0">
        {MEAL_CATEGORY_EMOJI[meal.category] ?? '🍽️'}
      </span>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-800 group-hover:text-primary transition-colors duration-150 truncate">
          {meal.name}
        </div>
        {meal.people?.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            {meal.people.map(person => (
              <span
                key={person}
                className="inline-block text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5 leading-none"
              >
                {person}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
    {/* Eye icon */}
    <button
      type="button"
      onClick={() => onPreview(meal)}
      className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-primary hover:bg-primary/10 transition-colors duration-150 opacity-0 group-hover:opacity-100 focus:opacity-100"
      aria-label="Aperçu"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    </button>
  </div>
);

// ── Current week section ─────────────────────────────────────────────────────

const ThisWeekSection: React.FC<{
  title: string;
  dateRange: string;
  days: moment.Moment[];
  meals: Meal[];
  onViewChange: (v: string) => void;
  onPreview: (meal: Meal) => void;
  highlightToday?: boolean;
}> = ({ title, dateRange, days, meals, onViewChange, onPreview, highlightToday = false }) => {
  const getMealsForDay = (day: moment.Moment) =>
    meals.filter(m => m.dateString === day.format('YYYY-MM-DD'));

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{dateRange}</span>
          <button
            type="button"
            onClick={() => onViewChange('planner')}
            className="text-sm text-primary hover:underline font-medium"
          >
            Planifier →
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {days.map(day => {
          const dayMeals = getMealsForDay(day);
          const isToday = highlightToday && day.isSame(moment(), 'day');
          const isPast = highlightToday && day.isBefore(moment(), 'day');

          return (
            <Card
              key={day.format('YYYY-MM-DD')}
              padding="sm"
              shadow="soft"
              className={isToday ? 'ring-2 ring-primary/40' : ''}
            >
              <div className="flex items-center gap-3">
                <div className="w-16 flex-shrink-0">
                  <div className={`text-sm font-semibold capitalize ${isToday ? 'text-primary' : 'text-gray-700'}`}>
                    {day.format('ddd')}
                  </div>
                  <div className="text-xs text-gray-400">{day.format('D MMM')}</div>
                </div>

                <div className="flex-1 min-w-0">
                  {dayMeals.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      {dayMeals.map(meal => (
                        <MealRow key={meal._id} meal={meal} onViewChange={onViewChange} onPreview={onPreview} />
                      ))}
                    </div>
                  ) : (
                    <span className={`text-sm ${isPast ? 'text-gray-300' : 'text-warning font-medium'}`}>
                      {isPast ? 'Aucun repas' : '⚠️ Rien de prévu'}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

// ── Next-week horizontal bubble section ──────────────────────────────────────

const NextWeekSection: React.FC<{
  days: moment.Moment[];
  meals: Meal[];
  onViewChange: (v: string) => void;
  onPreview: (meal: Meal) => void;
  nextMonday: moment.Moment;
}> = ({ days, meals, onViewChange, onPreview, nextMonday }) => {
  const getMealsForDay = (day: moment.Moment) =>
    meals.filter(m => m.dateString === day.format('YYYY-MM-DD'));

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">📅 Semaine prochaine</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">
            {nextMonday.format('D MMM')} – {nextMonday.clone().endOf('isoWeek').format('D MMM')}
          </span>
          <button
            type="button"
            onClick={() => onViewChange('planner')}
            className="text-sm text-primary hover:underline font-medium"
          >
            Planifier →
          </button>
        </div>
      </div>

      {/* Horizontal scrollable row of day bubbles */}
      <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-4 px-4">
        <div className="flex gap-2 pb-1" style={{ minWidth: 'max-content' }}>
          {days.map(day => {
            const dayMeals = getMealsForDay(day);
            const hasMeals = dayMeals.length > 0;
            const primaryMeal = dayMeals[0] ?? null;
            const colors = primaryMeal ? (CATEGORY_COLORS[primaryMeal.category] ?? { bg: '#f5f5f5', border: '#9e9e9e', text: '#37474f' }) : null;

            return (
              <button
                key={day.format('YYYY-MM-DD')}
                type="button"
                onClick={() => {
                  if (hasMeals && primaryMeal) {
                    onPreview(primaryMeal);
                  } else {
                    onViewChange('planner');
                  }
                }}
                className={`flex flex-col items-center rounded-2xl px-3 py-3 min-w-[76px] transition-all duration-150 border-2 ${
                  hasMeals
                    ? 'hover:opacity-80 active:scale-95'
                    : 'border-dashed hover:border-primary/40 hover:bg-primary/5'
                }`}
                style={
                  hasMeals && colors
                    ? { backgroundColor: colors.bg, borderColor: colors.border }
                    : { backgroundColor: '#fafafa', borderColor: '#e5e7eb' }
                }
              >
                {/* Day label */}
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${hasMeals && colors ? '' : 'text-gray-400'}`}
                  style={hasMeals && colors ? { color: colors.text } : undefined}
                >
                  {day.format('ddd')}
                </div>
                <div className={`text-xs mb-2 ${hasMeals && colors ? '' : 'text-gray-300'}`}
                  style={hasMeals && colors ? { color: colors.text + 'aa' } : undefined}
                >
                  {day.format('D')}
                </div>

                {/* Meal info or empty state */}
                {hasMeals && primaryMeal ? (
                  <>
                    <div className="text-xl leading-none mb-1">
                      {getCategoryEmoji(primaryMeal.category)}
                    </div>
                    <div
                      className="text-[10px] font-semibold text-center leading-tight max-w-[64px] truncate"
                      style={{ color: colors!.text }}
                    >
                      {primaryMeal.name !== primaryMeal.category ? primaryMeal.name : primaryMeal.category}
                    </div>
                    {dayMeals.length > 1 && (
                      <div className="text-[9px] mt-0.5" style={{ color: colors!.text + '99' }}>
                        +{dayMeals.length - 1}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-lg text-gray-200">+</div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────

const Dashboard: React.FC<DashboardProps> = ({ activeView, onViewChange }) => {
  const { currentListId } = useCurrentList();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [thisWeekMeals, setThisWeekMeals] = useState<Meal[]>([]);
  const [nextWeekMeals, setNextWeekMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewMeal, setPreviewMeal] = useState<Meal | null>(null);

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const thisMonday = moment().startOf('isoWeek').format('YYYY-MM-DD');
      const thisSunday = moment().endOf('isoWeek').format('YYYY-MM-DD');
      const nextMonday = moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
      const nextSunday = moment().add(1, 'week').endOf('isoWeek').format('YYYY-MM-DD');

      const [statsRes, thisWeekRes, nextWeekRes] = await Promise.all([
        currentListId ? groceryAPI.getStats(currentListId) : Promise.resolve(null),
        mealAPI.getAll({ startDate: thisMonday, endDate: thisSunday }),
        mealAPI.getAll({ startDate: nextMonday, endDate: nextSunday }),
      ]);

      if (statsRes && statsRes.success && statsRes.data) setStats(statsRes.data);
      else if (!currentListId) setStats(null);
      if (thisWeekRes.success && thisWeekRes.data) setThisWeekMeals(thisWeekRes.data);
      if (nextWeekRes.success && nextWeekRes.data) setNextWeekMeals(nextWeekRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildWeekDays = (weekStart: moment.Moment) =>
    Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days'));

  const thisMonday = moment().startOf('isoWeek');
  const nextMonday = moment().add(1, 'week').startOf('isoWeek');
  const thisWeekDays = buildWeekDays(thisMonday);
  const nextWeekDays = buildWeekDays(nextMonday);

  const unboughtImportant = stats?.importantItems.filter(item => !item.bought) ?? [];
  const shoppingTotal = (stats?.boughtItems ?? 0) + (stats?.toBuyItems ?? 0);
  const shoppingProgress = shoppingTotal > 0
    ? Math.round(((stats?.boughtItems ?? 0) / shoppingTotal) * 100)
    : 0;

  if (loading) {
    return (
      <>
        <MobileMenu activeView={activeView} onViewChange={onViewChange} />
        <div className="flex items-center justify-center py-24 text-gray-400">
          Chargement...
        </div>
      </>
    );
  }

  return (
    <>
      <MobileMenu activeView={activeView} onViewChange={onViewChange} />

      <main className="py-8">
        <Container maxWidth="xl">

          {/* ── Section 1: Cette semaine ── */}
          <ThisWeekSection
            title="🗓 Cette semaine"
            dateRange={`${thisMonday.format('D MMM')} – ${thisMonday.clone().endOf('isoWeek').format('D MMM')}`}
            days={thisWeekDays}
            meals={thisWeekMeals}
            onViewChange={onViewChange}
            onPreview={setPreviewMeal}
            highlightToday
          />

          {/* Shopping progress */}
          {stats && shoppingTotal > 0 && (
            <div className="mb-10">
              <Card padding="md" shadow="soft">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">🛒 Courses cette semaine</span>
                  <button
                    type="button"
                    onClick={() => onViewChange('list')}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Voir la liste →
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-success h-2 rounded-full transition-all duration-500"
                      style={{ width: `${shoppingProgress}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {stats.boughtItems} / {shoppingTotal}
                  </span>
                </div>
              </Card>
            </div>
          )}

          {/* ── Section 2: Semaine prochaine (horizontal bubbles) ── */}
          <NextWeekSection
            days={nextWeekDays}
            meals={nextWeekMeals}
            onViewChange={onViewChange}
            onPreview={setPreviewMeal}
            nextMonday={nextMonday}
          />

          {/* ── Section 3: Rappels ── */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">⚠️ Rappels</h2>
              <button
                type="button"
                onClick={() => onViewChange('list')}
                className="text-sm text-primary hover:underline font-medium"
              >
                Liste de courses →
              </button>
            </div>

            {unboughtImportant.length === 0 ? (
              <Card padding="md" shadow="soft">
                <p className="text-center text-success font-medium py-2">✅ Tout est prêt !</p>
              </Card>
            ) : (
              <div className="flex flex-col gap-2">
                {unboughtImportant.map(item => (
                  <Card key={item._id} padding="sm" shadow="soft">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{item.emoji}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-400">{item.type}</div>
                      </div>
                      <span className="ml-auto text-xs text-warning font-semibold">manquant</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </section>

        </Container>
      </main>

      <MealPreview meal={previewMeal} onClose={() => setPreviewMeal(null)} />
    </>
  );
};

export default Dashboard;
